import { useEffect, useState } from "react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";

export function Atms() {
  const [atms, setAtms] = useState([]);
  useEffect(() => { api.get("/atms").then((r) => setAtms(r.data.atms)).catch(() => {}); }, []);
  // world map projection: lng [-180..180]→[0..100%], lat [90..-90]→[0..100%]
  const proj = (lat, lng) => ({ left: `${((lng + 180) / 360) * 100}%`, top: `${((90 - lat) / 180) * 100}%` });
  return (
    <div className="space-y-6" data-testid="atms-panel">
      <div>
        <h1 className="font-display text-3xl">ATM Network · Cash-Redeemable Everywhere</h1>
        <p className="text-white/60 mt-2 text-sm">Every money order dispatched from the vault is redeemable at any partner ATM below.</p>
      </div>
      <div className="panel p-4 relative overflow-hidden" style={{aspectRatio:"2/1"}} data-testid="atm-map">
        <svg viewBox="0 0 360 180" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-25">
          {Array.from({length: 18}).map((_,i)=>(<line key={"h"+i} x1="0" y1={i*10} x2="360" y2={i*10} stroke="#00f0ff" strokeWidth="0.15"/>))}
          {Array.from({length: 36}).map((_,i)=>(<line key={"v"+i} x1={i*10} y1="0" x2={i*10} y2="180" stroke="#00f0ff" strokeWidth="0.15"/>))}
        </svg>
        {atms.map((a) => (
          <div key={a.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={proj(a.lat, a.lng)}>
            <div className={`w-2.5 h-2.5 rounded-full ${a.status==="online" ? "bg-[#39ff14]" : "bg-[#d4af37]"} shadow-[0_0_10px_currentColor]`}/>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-4 top-0 whitespace-nowrap font-mono text-[10px] text-white bg-black/80 border border-white/10 px-2 py-1 rounded">
              {a.id} · {a.city}
            </div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {atms.map((a) => (
          <div key={a.id} className="panel p-4" data-testid={`atm-${a.id}`}>
            <div className="flex justify-between items-center">
              <span className="font-mono text-xs text-[#d4af37]">{a.id}</span>
              <span className={`chip ${a.status==="online" ? "green" : "gold"}`}>{a.status}</span>
            </div>
            <div className="font-display text-lg mt-2">{a.city}, {a.country}</div>
            <div className="text-xs text-white/50 mt-1 font-mono">{a.network}</div>
            <div className="text-xs text-white/60 mt-2">{a.currencies.join(" · ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Compliance() {
  const [sm, setSm] = useState(null);
  const [lic, setLic] = useState([]);
  const [chain, setChain] = useState([]);
  useEffect(() => {
    api.get("/compliance/state-machine").then((r)=>setSm(r.data)).catch(()=>{});
    api.get("/compliance/licenses").then((r)=>setLic(r.data.licenses)).catch(()=>{});
    api.get("/compliance/audit-chain").then((r)=>setChain(r.data.chain)).catch(()=>{});
  }, []);
  return (
    <div className="space-y-8" data-testid="compliance-panel">
      <h1 className="font-display text-3xl">Compliance Console</h1>

      <section>
        <div className="label text-[#d4af37]">State Machine · S00 → S13</div>
        <div className="panel p-6 mt-3 grid md:grid-cols-2 gap-2">
          {sm?.states?.map((s, i) => (
            <div key={s.code} className="flex items-center gap-3 px-3 py-2 rounded border border-white/5" data-testid={`sm-${s.code}`}>
              <span className="font-mono text-[#d4af37] text-xs">{s.code}</span>
              <span className="text-sm">{s.name}</span>
              {i < (sm.states.length - 1) && <span className="ml-auto text-white/30 font-mono text-xs">↓</span>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="label text-[#d4af37]">Regulatory License Chain</div>
        <div className="panel mt-3">
          {lic.map((l, i) => (
            <div key={i} className="flex items-center justify-between px-6 py-3 border-b border-white/5" data-testid={`lic-${i}`}>
              <div>
                <div className="text-sm">{l.class}</div>
                <div className="font-mono text-[11px] text-white/40">{l.authority} · {l.number}</div>
              </div>
              <span className="chip green">{l.status}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="label text-[#d4af37]">Hash-Chained Audit Trail</div>
        <div className="panel mt-3">
          {chain.length === 0 && <div className="p-6 text-white/50 text-sm">No audit events yet. Send some money to populate the chain.</div>}
          {chain.map((e) => (
            <div key={e.id} className="px-6 py-3 border-b border-white/5" data-testid={`chain-${e.id}`}>
              <div className="flex justify-between text-xs font-mono">
                <span className="text-[#d4af37]">{e.action}</span>
                <span className="text-white/40">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-white/60 mt-1">{e.details}</div>
              <div className="mt-1 font-mono text-[10px] text-[#00f0ff] truncate">◈ {e.state_digest}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function StampTransferInline({ stamp, onDone }) {
  const [code, setCode] = useState(null);
  const gen = async () => {
    try { const { data } = await api.post(`/tax-stamps/${stamp.id}/transfer-code`); setCode(data.code); toast.success("Transfer code created — valid 24h"); onDone && onDone(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (code) return <div className="mt-3 font-mono text-[#00f0ff] text-xs" data-testid={`transfer-code-${stamp.id}`}>◈ {code}</div>;
  return <button data-testid={`transfer-btn-${stamp.id}`} onClick={gen} className="btn-ghost mt-2 text-xs w-full">Bearer Transfer →</button>;
}

export function ClaimStampBox({ onDone }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const claim = async (e) => {
    e.preventDefault(); if (!code.trim()) return; setLoading(true);
    try { await api.post("/tax-stamps/claim", { code: code.trim().toUpperCase() }); toast.success("Stamp claimed to your vault"); setCode(""); onDone && onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={claim} className="panel p-4 flex gap-3" data-testid="claim-form">
      <input data-testid="claim-code-input" className="input flex-1" placeholder="BSVM-XXXXXXXXXX" value={code} onChange={(e)=>setCode(e.target.value)}/>
      <button data-testid="claim-submit-btn" className="btn-primary" disabled={loading}>{loading ? "…" : "Claim"}</button>
    </form>
  );
}
